/**
 * useHostPublishReadiness — side-effect-free host compliance readiness for the
 * PUBLISH step of the listing wizard.
 *
 * The wizard no longer gates KYC/AML at step 1 (that ejected hosts to
 * /profile/verification before they saw a single field). Instead review.tsx
 * consults this at publish time: a compliant host publishes; a host we KNOW is
 * non-compliant has the finished listing saved as a draft and is routed to
 * finish verification. No data is lost either way.
 *
 * Unlike useVerificationGate / useAmlGate this hook fires NO toast and NO
 * redirect — review.tsx owns the post-submit navigation so the draft is saved
 * before we move the host anywhere.
 *
 * Host publish KYC is DUI + selfie only (`isHostPublishReady` /
 * `has_host_identity_for_listing`). Do NOT use `profile.isVerified` or
 * `isFullyVerified` here — those include driver license, which is a guest
 * requirement for booking/pay, not a host requirement for listing cars.
 *
 *  - KYC for publish = verification overview `isHostPublishReady`
 *  - AML host-ready = useAmlReadiness().isHostReady
 *  - Fails OPEN while loading: `resolved` stays false until we affirmatively
 *    know the answer, so a spinner or a cold cache never demotes a ready
 *    host's finished listing to a draft. The DB completeness trigger remains the
 *    authoritative barrier for actually reaching `active`.
 */
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { useAmlReadiness } from './use-aml-compliance'
import { verificationService } from '../services/verification.service'

export interface HostPublishReadiness {
  /** True once auth + KYC + AML have resolved (not loading). Gate only when true. */
  resolved: boolean
  /** KYC accepted AND AML host-ready. Only meaningful once `resolved`. */
  passed: boolean
  /** Resolved, and the KYC identity step is what's missing. */
  needsKyc: boolean
  /** Resolved, KYC ok, and the AML / Qualitas declaration is what's missing. */
  needsAml: boolean
}

export function useHostPublishReadiness(
  options: {
    /** Whether this listing will use Tago/Qualitas coverage. Unknown keeps the gate unresolved. */
    requiresPlatformCoverage?: boolean | null
  } = {},
): HostPublishReadiness {
  const { profile, isLoading: authLoading } = useAuth()
  const profileId = profile?.id ?? ''
  const aml = useAmlReadiness()

  // Shares the ['verification-gate', profileId] cache key with useVerificationGate
  // so the overview is not refetched when the host arrives from a KYC surface.
  const kycQuery = useQuery({
    queryKey: ['verification-gate', profileId],
    queryFn: () => verificationService.getOverview(profileId),
    enabled: !authLoading && !!profileId,
    staleTime: 15_000,
  })

  // Host publish: DUI + selfie. Never gate on isVerified / isFullyVerified
  // (those require a driver license for guests).
  const kycOk = kycQuery.data?.isHostPublishReady === true
  // Preserve the legacy contract for callers that cannot yet know the
  // listing's policy path (for example the first wizard screen). New callers
  // pass false explicitly for an own-policy vehicle; null remains the only
  // "still resolving" state.
  const requiresPlatformCoverage =
    options.requiresPlatformCoverage === undefined ? true : options.requiresPlatformCoverage
  const coverageKnown = requiresPlatformCoverage === true || requiresPlatformCoverage === false
  const amlReady = requiresPlatformCoverage === false ? true : aml.isHostReady

  // "Known" means we have a DEFINITIVE answer — not a spinner and not a network
  // error. We resolve on positive settlement (isSuccess), never on `!isLoading`:
  // a query that ERRORED also has isLoading === false, and treating an errored
  // AML fetch as "not ready" would let a transient network blip permanently
  // demote a ready host's finished listing to a draft. On loading OR error we
  // stay unresolved and fail OPEN — the DB completeness trigger is the real
  // barrier for `active`, so no unverified car actually goes live meanwhile.
  const kycKnown = !authLoading && !!profile && kycQuery.isSuccess
  const amlKnown = requiresPlatformCoverage === false ? true : aml.isSuccess
  const resolved = kycKnown && amlKnown && coverageKnown
  const passed = kycOk && amlReady

  return {
    resolved,
    passed,
    needsKyc: resolved && !kycOk,
    needsAml: resolved && kycOk && !amlReady,
  }
}
